import { useFeatureValue } from '@growthbook/growthbook-react';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { DealFormPage } from '@openx/components-deal/lib/DealForm/DealFormPage.tsx';
import { useDealContext } from '@openx/components-deal/lib/utils/context/DealContext.tsx';
import { PageHeader } from '@openx/components-header/PageHeader.tsx';
import { ChangesFromInfoTag } from '@openx/components-targeting/Targeting/shared/ChangesFromInfoTag.tsx';
import { ALLOWLIST_FREQUENCY_CAPPING, ENABLED_FREQUENCY_CAPPING } from '@openx/constants/feature-flag-keys.ts';
import { useEmailAllowlist } from '@openx/utils/hooks/useEmailAllowlist.ts';
import { useSnackbar } from 'notistack';
import { type JSX, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useAuth } from '../../components/AuthContext/context.ts';
import { GROWTHBOOK_SALT } from '../../config.ts';
import { DealFormFields } from './DealForm/DealFormFields.tsx';

export const EditDealPage = (): JSX.Element | null => {
  const { session } = useAuth();
  const { t } = useTranslation();
  const { dealUid } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isFrequencyCappingEnabled = useFeatureValue(ENABLED_FREQUENCY_CAPPING, false);
  const allowListFrequencyCapping: string = useFeatureValue(ALLOWLIST_FREQUENCY_CAPPING, '');

  const { useDealFetch } = useDealContext();
  const { deal } = useDealFetch(dealUid ?? '');
  const isDirectDeal = !!deal?.is_direct_deal;

  const { inAllowList: inAllowListFrequencyCapping } = useEmailAllowlist({
    email: session?.email,
    allowList: allowListFrequencyCapping,
    salt: GROWTHBOOK_SALT,
  });

  const isAllowFrequencyCapping =
    isFrequencyCappingEnabled &&
    (!allowListFrequencyCapping.length || (!!allowListFrequencyCapping.length && inAllowListFrequencyCapping));

  const isEdit = !!dealUid;
  const title = isEdit ? t('Edit') : t('Create Deal');

  useEffect(() => {
    if (isDirectDeal) {
      enqueueSnackbar(t('This action is not available for direct deals.'), { variant: 'error' });
      navigate(`/deals/${dealUid}/details`, { replace: true });
    }
  }, [isDirectDeal, dealUid, navigate, enqueueSnackbar, t]);

  if (isDirectDeal) {
    return <Loader />;
  }

  return (
    <PageHeader
      title={title}
      titleBadge={<ChangesFromInfoTag fieldName="create-deal" titleName={t('Create Deal')} />}
      titlePrefix="OpenXSelect"
    >
      <DealFormPage
        id={dealUid}
        renderFields={() => <DealFormFields isEdit={isEdit} isAllowFrequencyCapping={isAllowFrequencyCapping} />}
      />
    </PageHeader>
  );
};
